/**
 * SatellitesPanel v2.4 (AK-73) — компактная нижняя панель карты, 2 ряда.
 *
 * Изменения относительно v3 (AK-58/59, commit 141ec14e):
 *   1. Inline-легенда УДАЛЕНА — переехала в <MapVerticalLegend/> (top-3 right-3).
 *   2. Drag handle ⋮⋮ слева от пилок продуктов (cursor:move) — позволяет
 *      перетащить панель в любое место карты. Позиция персистится в
 *      useMapStore.satellitesPanelPosition (по умолчанию null = bottom-center
 *      via CSS left:50%; -translate-x-1/2; bottom:12px).
 *   3. [−] Collapse кнопка справа после калибровки (через separator). Клик:
 *      - setSatellitesPanelCollapsed(true)
 *      - setMode('none') (выключает все спутниковые продукты)
 *      - setShowCloudMask(false), setShowTCI(false)
 *      Затем парент-компонент отрисует <SatellitesRestorePill/> вместо панели.
 *   4. Отдельный wrapper <SatellitesPanelOrRestorePill/> экспортируется тут же —
 *      переключает между панелью и floating restore-pill «🛰 Спутники».
 *
 * Layout (max-width 944px):
 *   Row 1: [⋮⋮ drag] · 5 product pills · [⇆] [☁] [📷] [⚙] · | · [−]
 *   Row 2: ← 2025 · | · months · | · days
 *
 * Mobile (<768px) — wrap допустим (flex-wrap), draggable отключён, рендерится
 * inline-карточка через mobile=true проп (без absolute, без drag handle, с
 * обычным title bar — fallback к DraggablePanel-like styling).
 *
 * Spec: docs/superpowers/prototypes/satellites-panel-v2/v2.4.html.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Settings2,
  ArrowLeftRight,
  GripVertical,
  Minus,
  Cloud,
  Camera,
  Satellite,
  ChevronLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMapStore, type NdviMode } from '@/lib/map/useMapStore';
import { cn } from '@/lib/utils';

interface SatellitesPanelProps {
  mobile?: boolean;
  /** Список доступных дат снимков (опц). По умолчанию — последние 8 недель. */
  availableDates?: string[];
}

const LAYER_PILLS: Array<{ id: NdviMode; label: string }> = [
  { id: 'ndvi', label: 'NDVI' },
  { id: 'avg_ndvi', label: 'NDVI сред.' },
  { id: 'ndvi_contrast', label: 'NDVI контр.' },
  { id: 'gnr', label: 'GNR' },
  { id: 'uniformity', label: 'Однородн.' },
];

/** Default fallback — последние 8 пятниц (S2 пролёты раз в 5 дней). */
function defaultDates(): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < 8; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - i * 7);
    out.push(d.toISOString().slice(0, 10));
  }
  return out.reverse();
}

interface MonthBucket {
  key: string;
  label: string;
  ts: number;
  dates: string[];
}

function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

function buildMonthBuckets(dates: string[]): MonthBucket[] {
  const map = new Map<string, MonthBucket>();
  for (const iso of dates) {
    if (!iso || iso.length < 7) continue;
    const key = monthKey(iso);
    const existing = map.get(key);
    if (existing) {
      existing.dates.push(iso);
    } else {
      const [y, m] = key.split('-');
      const d = new Date(Number(y), Number(m) - 1, 1);
      // AK-73 founder feedback: год показан слева как «← 2025»,
      // на месяцах год не дублируем → label «янв.», «фев.», ...
      const label = d.toLocaleDateString('ru-RU', { month: 'short' });
      map.set(key, { key, label, ts: d.getTime(), dates: [iso] });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.ts - b.ts);
}

function dayLabel(iso: string): string {
  return iso.slice(8, 10);
}

// =====================================================================
// useDragPanel — мини-хук для перетаскивания SatellitesPanel.
// Возвращает refs/handlers + текущую позицию (или null = default CSS).
// =====================================================================

interface DragHandle {
  /** Ref на root-элемент панели (для constrain'а к parent rect). */
  panelRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Ref на drag-handle (⋮⋮) — на нём вешается onPointerDown. */
  onPointerDown: (e: React.PointerEvent) => void;
  /** Текущая позиция (если null — компонент использует CSS default). */
  position: { x: number; y: number } | null;
  /** Идёт ли drag — для cursor/shadow эффектов. */
  dragging: boolean;
}

function useDragPanel(disabled: boolean): DragHandle {
  const position = useMapStore((s) => s.satellitesPanelPosition);
  const setPosition = useMapStore((s) => s.setSatellitesPanelPosition);

  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const next = {
        x: Math.max(0, dragRef.current.origX + dx),
        y: Math.max(0, dragRef.current.origY + dy),
      };
      const parent = panelRef.current?.parentElement;
      const node = panelRef.current;
      if (parent && node) {
        const pBox = parent.getBoundingClientRect();
        const nBox = node.getBoundingClientRect();
        next.x = Math.min(next.x, pBox.width - nBox.width);
        next.y = Math.min(next.y, pBox.height - nBox.height);
      }
      setPosition(next);
    },
    [setPosition],
  );

  const onPointerUp = useCallback(() => {
    setDragging(false);
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
  }, [onPointerMove]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      e.preventDefault();
      // Если позиция ещё default (null), захардкодим текущие координаты
      // panel'а — иначе после первого drag'а панель «прыгнет» из CSS-default
      // в (0, 0) перед движением.
      const node = panelRef.current;
      const parent = node?.parentElement;
      let origX = position?.x ?? 0;
      let origY = position?.y ?? 0;
      if (position == null && node && parent) {
        const pBox = parent.getBoundingClientRect();
        const nBox = node.getBoundingClientRect();
        origX = nBox.left - pBox.left;
        origY = nBox.top - pBox.top;
      }
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX,
        origY,
      };
      setDragging(true);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    },
    [disabled, position, onPointerMove, onPointerUp],
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  return { panelRef, onPointerDown, position, dragging };
}

// =====================================================================
// Main component
// =====================================================================

export function SatellitesPanel({
  mobile = false,
  availableDates,
}: SatellitesPanelProps) {
  const mode = useMapStore((s) => s.mode);
  const setMode = useMapStore((s) => s.setMode);
  const selectedDate = useMapStore((s) => s.selectedDate);
  const setSelectedDate = useMapStore((s) => s.setSelectedDate);
  const setCalibrationOpen = useMapStore((s) => s.setCalibrationOpen);
  const compareMode = useMapStore((s) => s.compareMode);
  const setCompareMode = useMapStore((s) => s.setCompareMode);
  const showCloudMask = useMapStore((s) => s.showCloudMask);
  const setShowCloudMask = useMapStore((s) => s.setShowCloudMask);
  const showTCI = useMapStore((s) => s.showTCI);
  const setShowTCI = useMapStore((s) => s.setShowTCI);
  const setCollapsed = useMapStore((s) => s.setSatellitesPanelCollapsed);

  const drag = useDragPanel(mobile);

  const dates = useMemo(() => availableDates ?? defaultDates(), [availableDates]);
  const months = useMemo(() => buildMonthBuckets(dates), [dates]);

  const [activeMonthKey, setActiveMonthKey] = useState<string | null>(null);

  useEffect(() => {
    if (months.length === 0) {
      setActiveMonthKey(null);
      return;
    }
    const selectedMonth = selectedDate ? monthKey(selectedDate) : null;
    const hasSelected =
      selectedMonth && months.some((m) => m.key === selectedMonth);
    if (hasSelected) {
      setActiveMonthKey((prev) => (prev === selectedMonth ? prev : selectedMonth));
    } else {
      const last = months[months.length - 1].key;
      setActiveMonthKey((prev) => (prev === last ? prev : last));
    }
  }, [months, selectedDate]);

  const activeMonth = useMemo(
    () => months.find((m) => m.key === activeMonthKey) ?? null,
    [months, activeMonthKey],
  );

  const handlePillClick = (id: NdviMode) => {
    setMode(mode === id ? 'none' : id);
  };

  const toggleCompare = () => {
    if (compareMode.enabled) {
      setCompareMode({ enabled: false, dateA: null, dateB: null });
    } else {
      const idx = dates.indexOf(selectedDate);
      const prev = idx > 0 ? dates[idx - 1] : dates[0];
      setCompareMode({ enabled: true, dateA: prev, dateB: selectedDate });
    }
  };

  const handleCollapse = () => {
    // При сворачивании — выключаем все спутниковые продукты, как просил founder.
    // (lastNdviMode сохранён внутри setMode'а перед этим, и при restore вернётся.)
    setMode('none');
    setShowCloudMask(false);
    setShowTCI(false);
    setCollapsed(true);
  };

  // Mobile рендер — простая card без absolute/drag, для inline стека под картой.
  if (mobile) {
    return (
      <div className="rounded-xl bg-card/95 backdrop-blur border border-border shadow-sm">
        <div className="px-3 py-2 border-b border-border/60 text-sm font-semibold text-foreground flex items-center gap-2">
          <Satellite className="h-4 w-4 text-muted-foreground" aria-hidden />
          Спутники
        </div>
        <PanelBody
          mode={mode}
          onPillClick={handlePillClick}
          showCloudMask={showCloudMask}
          setShowCloudMask={setShowCloudMask}
          showTCI={showTCI}
          setShowTCI={setShowTCI}
          compareEnabled={compareMode.enabled}
          onToggleCompare={toggleCompare}
          onOpenCalibration={() => setCalibrationOpen(true)}
          onCollapse={handleCollapse}
          months={months}
          activeMonthKey={activeMonthKey}
          setActiveMonthKey={setActiveMonthKey}
          activeMonth={activeMonth}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          mobile
        />
      </div>
    );
  }

  // Desktop draggable. Если position === null — bottom-center via CSS classes;
  // как только пользователь перетащил, переключаемся на inline left/top.
  const positioned = drag.position != null;

  return (
    <div
      ref={drag.panelRef}
      className={cn(
        'absolute z-10 rounded-xl bg-card/95 backdrop-blur border border-border shadow-lg select-none',
        'w-[944px] max-w-[calc(100%-24px)]',
        !positioned && 'left-1/2 -translate-x-1/2 bottom-3',
        drag.dragging && 'shadow-2xl',
      )}
      style={
        positioned
          ? { left: drag.position!.x, top: drag.position!.y }
          : undefined
      }
    >
      {/* Row 1: [drag] products … [icons] | [−] */}
      <div className="flex items-center gap-1 px-1 py-2 border-b border-border/60 flex-wrap">
        {/* Drag handle (left edge) */}
        <button
          type="button"
          onPointerDown={drag.onPointerDown}
          className={cn(
            'w-6 h-7 rounded hover:bg-accent flex items-center justify-center text-muted-foreground shrink-0',
            drag.dragging ? 'cursor-grabbing' : 'cursor-grab',
          )}
          title="Перетащить панель"
          aria-label="Перетащить панель"
        >
          <GripVertical className="h-3.5 w-3.5" aria-hidden />
        </button>

        {/* Product pills */}
        {LAYER_PILLS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => handlePillClick(p.id)}
            className={cn(
              'px-3 py-1 rounded text-xs font-medium border transition-colors shrink-0',
              mode === p.id
                ? 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-700'
                : 'bg-background text-foreground border-border hover:bg-accent',
            )}
          >
            {p.label}
          </button>
        ))}

        <div className="flex-1" />

        {/* Action icons */}
        <Button
          type="button"
          variant={compareMode.enabled ? 'default' : 'ghost'}
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={toggleCompare}
          title="Сравнить даты"
          aria-label="Сравнить даты"
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant={showCloudMask ? 'default' : 'ghost'}
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => setShowCloudMask(!showCloudMask)}
          title="Маска облачности"
          aria-label="Маска облачности"
        >
          <Cloud className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant={showTCI ? 'default' : 'ghost'}
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => setShowTCI(!showTCI)}
          title="Снимок (TCI)"
          aria-label="Снимок (TCI)"
        >
          <Camera className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => setCalibrationOpen(true)}
          title="Калибровка"
          aria-label="Калибровка"
        >
          <Settings2 className="h-3.5 w-3.5" />
        </Button>

        {/* Vertical separator before [−] */}
        <span className="mx-1 h-5 w-px bg-border shrink-0" aria-hidden />

        {/* Collapse [−] */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={handleCollapse}
          title="Скрыть панель (выключит все спутниковые продукты)"
          aria-label="Скрыть панель"
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Row 2: months | days */}
      <DateRow
        months={months}
        activeMonthKey={activeMonthKey}
        setActiveMonthKey={setActiveMonthKey}
        activeMonth={activeMonth}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />
    </div>
  );
}

// =====================================================================
// Sub-components
// =====================================================================

interface PanelBodyProps {
  mode: NdviMode;
  onPillClick: (id: NdviMode) => void;
  showCloudMask: boolean;
  setShowCloudMask: (on: boolean) => void;
  showTCI: boolean;
  setShowTCI: (on: boolean) => void;
  compareEnabled: boolean;
  onToggleCompare: () => void;
  onOpenCalibration: () => void;
  onCollapse: () => void;
  months: MonthBucket[];
  activeMonthKey: string | null;
  setActiveMonthKey: (k: string) => void;
  activeMonth: MonthBucket | null;
  selectedDate: string;
  setSelectedDate: (d: string) => void;
  mobile?: boolean;
}

function PanelBody({
  mode,
  onPillClick,
  showCloudMask,
  setShowCloudMask,
  showTCI,
  setShowTCI,
  compareEnabled,
  onToggleCompare,
  onOpenCalibration,
  onCollapse,
  months,
  activeMonthKey,
  setActiveMonthKey,
  activeMonth,
  selectedDate,
  setSelectedDate,
}: PanelBodyProps): ReactNode {
  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60 flex-wrap">
        {LAYER_PILLS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPillClick(p.id)}
            className={cn(
              'px-3 py-2 rounded text-xs font-medium border transition-colors',
              mode === p.id
                ? 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-700'
                : 'bg-background text-foreground border-border hover:bg-accent',
            )}
          >
            {p.label}
          </button>
        ))}
        <div className="flex-1" />
        <Button
          type="button"
          variant={compareEnabled ? 'default' : 'outline'}
          size="sm"
          onClick={onToggleCompare}
          className="gap-2 shrink-0 h-7 px-3 text-xs"
        >
          <ArrowLeftRight className="h-3.5 w-3.5" />
          Сравнить
        </Button>
        <Button
          type="button"
          variant={showCloudMask ? 'default' : 'ghost'}
          size="icon"
          className="h-7 w-7"
          onClick={() => setShowCloudMask(!showCloudMask)}
          title="Облачность"
          aria-label="Облачность"
        >
          <Cloud className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant={showTCI ? 'default' : 'ghost'}
          size="icon"
          className="h-7 w-7"
          onClick={() => setShowTCI(!showTCI)}
          title="Снимок"
          aria-label="Снимок"
        >
          <Camera className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-2 shrink-0 h-7 px-2 text-xs"
          onClick={onOpenCalibration}
        >
          <Settings2 className="h-3.5 w-3.5" />
          Калибровка
        </Button>
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onCollapse}
          title="Скрыть"
          aria-label="Скрыть"
        >
          <Minus className="h-3.5 w-3.5" />
        </Button>
      </div>
      <DateRow
        months={months}
        activeMonthKey={activeMonthKey}
        setActiveMonthKey={setActiveMonthKey}
        activeMonth={activeMonth}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />
    </>
  );
}

interface DateRowProps {
  months: MonthBucket[];
  activeMonthKey: string | null;
  setActiveMonthKey: (k: string) => void;
  activeMonth: MonthBucket | null;
  selectedDate: string;
  setSelectedDate: (d: string) => void;
}

/** Year of the active season — derived from activeMonthKey or selectedDate.
 * AK-73 founder feedback v2.4: panel показывает только месяцы активного года,
 * слева пассивный indicator «← 2025» (год сезона). Cross-year navigation —
 * TBD через global SeasonSelector (не из этой панели).
 */
function deriveActiveYear(
  activeMonthKey: string | null,
  selectedDate: string,
  months: MonthBucket[],
): number | null {
  if (activeMonthKey) {
    const y = Number(activeMonthKey.slice(0, 4));
    if (!Number.isNaN(y)) return y;
  }
  if (selectedDate && selectedDate.length >= 4) {
    const y = Number(selectedDate.slice(0, 4));
    if (!Number.isNaN(y)) return y;
  }
  if (months.length > 0) {
    const y = Number(months[months.length - 1].key.slice(0, 4));
    if (!Number.isNaN(y)) return y;
  }
  return null;
}

function DateRow({
  months,
  activeMonthKey,
  setActiveMonthKey,
  activeMonth,
  selectedDate,
  setSelectedDate,
}: DateRowProps): ReactNode {
  const activeYear = deriveActiveYear(activeMonthKey, selectedDate, months);
  const monthsThisYear = useMemo(
    () =>
      activeYear == null
        ? months
        : months.filter((m) => m.key.startsWith(`${activeYear}-`)),
    [months, activeYear],
  );

  return (
    <div className="flex items-center gap-2 px-2 py-2 text-xs">
      {months.length === 0 ? (
        <span className="text-muted-foreground italic">
          Нет доступных снимков
        </span>
      ) : (
        <>
          {/* Year indicator (passive). Cross-year switch — through global SeasonSelector. */}
          {activeYear != null && (
            <div
              className="flex items-center gap-1 text-muted-foreground font-medium shrink-0"
              title="Год сезона. Переключение — сверху-справа"
            >
              <ChevronLeft className="h-2.5 w-2.5" aria-hidden />
              <span>{activeYear}</span>
            </div>
          )}
          <span className="text-slate-300 dark:text-slate-600 shrink-0" aria-hidden>|</span>
          <div className="flex items-center gap-1 shrink-0">
            {monthsThisYear.map((m) => {
              const isActive = m.key === activeMonthKey;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setActiveMonthKey(m.key)}
                  className={cn(
                    'px-1 py-1 rounded transition-colors shrink-0',
                    isActive
                      ? 'bg-emerald-700 text-white font-medium hover:bg-emerald-800'
                      : 'text-muted-foreground hover:bg-accent',
                  )}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
          {activeMonth && (
            <span className="text-slate-300 dark:text-slate-600 shrink-0" aria-hidden>|</span>
          )}
          {activeMonth ? (
            <div className="flex items-center gap-1 flex-wrap">
              {activeMonth.dates.map((iso) => {
                const isActive = selectedDate === iso;
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setSelectedDate(iso)}
                    className={cn(
                      'px-1 py-1 rounded font-mono transition-colors shrink-0',
                      isActive
                        ? 'bg-slate-800 text-white'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                    )}
                  >
                    {dayLabel(iso)}
                  </button>
                );
              })}
            </div>
          ) : (
            <span className="text-muted-foreground italic ml-1">
              Выберите месяц
            </span>
          )}
        </>
      )}
    </div>
  );
}

// =====================================================================
// Restore-pill: floating button когда панель свёрнута.
// =====================================================================

export function SatellitesRestorePill() {
  const setCollapsed = useMapStore((s) => s.setSatellitesPanelCollapsed);
  const setMode = useMapStore((s) => s.setMode);
  const lastNdviMode = useMapStore((s) => s.lastNdviMode);

  const handleClick = () => {
    setCollapsed(false);
    // Восстанавливаем последний non-'none' mode (если он был — иначе ndvi default).
    const restoreMode: NdviMode = lastNdviMode !== 'none' ? lastNdviMode : 'ndvi';
    setMode(restoreMode);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'absolute z-10 left-1/2 -translate-x-1/2 bottom-3',
        'bg-card/95 backdrop-blur rounded-full shadow-lg border border-border',
        'px-3 py-2 flex items-center gap-2 hover:bg-card transition-colors',
      )}
      title="Показать панель спутников"
      aria-label="Показать панель спутников"
    >
      <Satellite className="h-3.5 w-3.5 text-foreground" aria-hidden />
      <span className="text-xs text-foreground font-medium">Спутники</span>
    </button>
  );
}

// =====================================================================
// Wrapper: панель vs restore-pill (по collapse state).
// =====================================================================

export function SatellitesPanelOrRestorePill({
  mobile = false,
  availableDates,
}: SatellitesPanelProps) {
  const collapsed = useMapStore((s) => s.satellitesPanelCollapsed);
  // На mobile collapse-флаг игнорим — там всегда стек панелей под картой,
  // restore-pill в маленькой колонке смысла не имеет.
  if (collapsed && !mobile) {
    return <SatellitesRestorePill />;
  }
  return <SatellitesPanel mobile={mobile} availableDates={availableDates} />;
}

export default SatellitesPanel;
